import { sortBy } from 'lodash';
import {
    useCallback,
    useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { MAPPING_ON_OLD_AND_NEW_ASSET_CLASS_EXTERNAL_IDS, priorityOfAssetClassMapping, RISK_INDICATOR_CONFIG } from '../../../../../constants/constants';
import type {
    ModelPortfolioDetailsType, IndividualPortfolioDataType, ModelPortfolioAllocationType, AssetClassTreeType,
} from '../../../../../ViewModels/ModelPortfolioDetails';
import { BodyItemType, FooterItemType } from '../../../../../ViewModels/ModelPortfolioDetails';

type AllocationType = {
    name: string,
    value: number,
    color: string,
    ExternalId: string
}

type ProductAllocationModelType = {
    allocations: Array<AllocationType>,
    modelPortfolioDetails: ModelPortfolioDetailsType,
    factsheetUrl: string,
    isLoading: boolean
    error: any,
    riskScore: number,
}

type UseProductAllocationPayloadType = {
    productId?: number,
    individualPortfolioData: IndividualPortfolioDataType<Partial<BodyItemType>, Partial<FooterItemType>>,
    doNotCalculateAllocations?: boolean,
}

function flattenWithChildren(arr) {
    const result:any [] = [];

    function traverse(node) {
        if (node.Data) {
            result.push({ Data: node.Data });
        }

        if (node.Children?.length) {
            node.Children.forEach(traverse);
        }
    }

    arr.forEach(traverse);

    return result;
}

const sortAssetClassesByExternalId = (assetClassTree) => {
    const flattenAssetClassTree = flattenWithChildren(assetClassTree);
    const assetClassesWithExternalId = flattenAssetClassTree?.filter(item => item.Data?.ExternalId);
    const isAllExternalIdsNumaric = assetClassesWithExternalId?.filter(id => !['Equity', 'Gold']?.includes(id?.Data?.ExternalId))?.every(id => !Number.isNaN(Number(id?.Data?.ExternalId)));

    const sortedAssetClasses = isAllExternalIdsNumaric ? sortBy(assetClassesWithExternalId, (item) => parseInt(item.Data?.ExternalId, 10)) : assetClassesWithExternalId.sort((a, b) => {
        const aKey = a.Data.ExternalId?.toLowerCase();
        const bKey = b.Data.ExternalId?.toLowerCase();

        const indexA = priorityOfAssetClassMapping.findIndex(p => p.toLowerCase() === aKey);
        const indexB = priorityOfAssetClassMapping.findIndex(p => p.toLowerCase() === bKey);

        return indexA - indexB;
    });

    return sortedAssetClasses;
};

export const useProductAllocation = ({ productId, individualPortfolioData, doNotCalculateAllocations = false }: UseProductAllocationPayloadType) : ProductAllocationModelType => {
    const [overrideAllocations, setOverrideAllocations] = useState<Array<AllocationType> | null>(null);

    const { i18n: { language } } = useTranslation();

    const {
        data: assetClassTreeResponse,
        isLoading: isAssetClassTreeLoading,
        error: assetClassTreeError,
    } = useGetCustomService({
        service: 'portfolioManagementLocal',
        api: 'getAssetClassTree',
        payload: language,
        enabled: true,
    });

    const assetClassTree: Array<AssetClassTreeType> = assetClassTreeResponse?.data ?? [];

    const {
        data: allocationsResponse,
        isLoading: isAllocationsLoading,
        error: allocationsError,
    } = useGetCustomService({
        service: 'portfolioManagementLocal',
        api: 'getModelPortfolioAllocations',
        payload: [productId, { language }],
        enabled: !!productId,
    });

    const {
        data: detailsResponse,
        isLoading: isDetailsLoading,
        error: detailsError,
    } = useGetCustomService({
        service: 'portfolioManagementLocal',
        api: 'getModelPortfolioDetails',
        payload: [productId, { language }],
        enabled: !!productId,
    });

    const mutation = useGenericAPIMutation();

    const isLoading = !!productId && (isAssetClassTreeLoading || isAllocationsLoading || isDetailsLoading);
    const error = assetClassTreeError || allocationsError || detailsError || mutation.error || null;

    const modelPortfolioDetails: ModelPortfolioDetailsType = productId ? (detailsResponse?.data ?? <ModelPortfolioDetailsType>{}) : <ModelPortfolioDetailsType>{};
    const factsheetUrl = productId ? (detailsResponse?.data?.Factsheet?.replace('url:', '') ?? '') : '';

    const fetchedAllocations = useMemo(() => {
        if (!productId || assetClassTree?.length < 1) {
            return [];
        }

        const assetClassAllocations = allocationsResponse?.data?.ModelPortfolioAllocations
            ?.map(({ Category, Breakdowns }) => ({
                category: Category,
                breakdowns:
                Breakdowns?.map(({ Id, Allocation }) => ({
                    id: Id,
                    allocation: Allocation,
                })) ?? [],
            }))
            ?.find(({ category }) => category === 'AssetClass')
            ?.breakdowns ?? [];

        const orderedInstruments = sortAssetClassesByExternalId(assetClassTree);

        return orderedInstruments
            .map((item) => ({
                name: item?.Data?.Name ?? '',
                value:
                assetClassAllocations.find((i) => item.Data?.Id === i.id)
                    ?.allocation ?? null,
                color: item?.Data?.Color ?? '',
                ExternalId: item?.Data?.ExternalId ?? '',
            }))
            .filter(({ value }) => value != null);
    }, [productId, assetClassTree, allocationsResponse]);

    const allocations = overrideAllocations ?? fetchedAllocations;

    useEffect(() => {
        setOverrideAllocations(null);
    }, [productId]);

    const riskScore = useMemo(() => {
        const predefinedRiskScore = JSON.parse(modelPortfolioDetails.Attributes || '{}')?.InvestmentRisk;

        if (predefinedRiskScore) {
            return predefinedRiskScore;
        }
        const sumOfAllEquityAssets = (allocations?.filter(f => [
            '5', '6',
            MAPPING_ON_OLD_AND_NEW_ASSET_CLASS_EXTERNAL_IDS.get('4a'),
            MAPPING_ON_OLD_AND_NEW_ASSET_CLASS_EXTERNAL_IDS.get('4b'),
        ].includes(`${f.ExternalId}`))
            ?.reduce((acc, curr) => acc + curr.value, 0) ?? 0) * 100;

        const closestUpperID = RISK_INDICATOR_CONFIG.filter(obj => obj.limit >= parseFloat(sumOfAllEquityAssets.toFixed(2)))
            .reduce((prev, curr) => (prev.limit < curr.limit ? prev : curr))
            .value;

        return closestUpperID;
    }, [allocations, modelPortfolioDetails]);

    const updateAllocations = useCallback((data: Array<ModelPortfolioAllocationType>) => {
        const allocationsValues = data?.find(f => f.category === 'AssetClass')?.breakdowns.map(i => ({ id: i.id, value: i.allocation }));
        const orderedInstruments = sortAssetClassesByExternalId(assetClassTree);
        const instrumentAllocations = orderedInstruments?.map(item => ({
            name: item?.Data?.Name,
            value: allocationsValues?.find(i => item.Data?.Id === i.id)?.value,
            color: item?.Data?.Color,
            ExternalId: item?.Data?.ExternalId,
        }));

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setOverrideAllocations(instrumentAllocations?.filter(f => !!f.value));
    }, [assetClassTree]);

    const calculateNewAllocations = async () => {
        const payload = individualPortfolioData?.body?.filter(f => !f?.allocation?.disabled).map(i => ({ securityId: i.id, allocation: (i?.allocation?.value ?? 0) / 100 }));

        if (payload?.length < 1) {
            return;
        }
        const response: any = await mutation.mutateAsync({
            service: 'customInvestmentService',
            API: 'calculateModelPortfolioAllocations',
            payload: { modelPortfolioId: modelPortfolioDetails.Id, data: payload },
        });

        updateAllocations(response?.data?.modelPortfolioAllocations);
    };

    useEffect(() => {
        if (individualPortfolioData?.footer?.allocation === 100 && modelPortfolioDetails.Id && !doNotCalculateAllocations) {
            calculateNewAllocations();
        }
    }, [individualPortfolioData, modelPortfolioDetails.Id, doNotCalculateAllocations]);

    return {
        allocations, modelPortfolioDetails, factsheetUrl, isLoading, error, riskScore,
    };
};
